/**
 * Input sanitization utilities to prevent XSS attacks.
 * 
 * Uses DOMPurify to sanitize user-controlled data before rendering.
 */

import DOMPurify from 'dompurify';

/**
 * Sanitize HTML string to prevent XSS attacks.
 * 
 * Removes potentially dangerous HTML tags and attributes while
 * preserving safe formatting.
 * 
 * @param dirty - Untrusted HTML string
 * @returns Sanitized HTML string safe for rendering
 */
export function sanitizeHtml(dirty: string): string {
  return DOMPurify.sanitize(dirty, {
    ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'a', 'p', 'br'],
    ALLOWED_ATTR: ['href', 'title'],
    ALLOW_DATA_ATTR: false,
  });
}

/**
 * Sanitize plain text by removing all HTML tags.
 * 
 * Use this for user input that should be displayed as plain text
 * (e.g., names, emails, descriptions).
 * 
 * @param dirty - Untrusted string that may contain HTML
 * @returns Plain text with all HTML removed
 */
export function sanitizeText(dirty: string): string {
  return DOMPurify.sanitize(dirty, {
    ALLOWED_TAGS: [],
    ALLOWED_ATTR: [],
  });
}

/**
 * Sanitize user display name.
 * 
 * Removes HTML tags and limits length to prevent UI issues.
 * 
 * @param name - User's display name
 * @param maxLength - Maximum length (default: 100)
 * @returns Sanitized name
 */
export function sanitizeUserName(name: string | undefined | null, maxLength: number = 100): string {
  if (!name) {
    return 'Unknown User';
  }
  
  const sanitized = sanitizeText(name);
  
  if (sanitized.length > maxLength) {
    return sanitized.substring(0, maxLength) + '...';
  }
  
  return sanitized;
}

/**
 * Sanitize email address.
 * 
 * Removes HTML tags and validates basic email format.
 * 
 * @param email - Email address
 * @returns Sanitized email or empty string if invalid
 */
export function sanitizeEmail(email: string | undefined | null): string {
  if (!email) {
    return '';
  }
  
  const sanitized = sanitizeText(email);
  
  // Basic email validation
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(sanitized)) {
    return '';
  }
  
  return sanitized;
}

/**
 * Sanitize URL to prevent javascript: and data: URLs.
 * 
 * Only allows http:, https:, and mailto: protocols.
 * 
 * @param url - URL string
 * @returns Sanitized URL or empty string if invalid
 */
export function sanitizeUrl(url: string | undefined | null): string {
  if (!url) {
    return '';
  }
  
  const sanitized = sanitizeText(url);
  
  // Check for allowed protocols
  const allowedProtocols = ['http:', 'https:', 'mailto:'];
  
  try {
    const urlObj = new URL(sanitized);
    if (allowedProtocols.includes(urlObj.protocol)) {
      return sanitized;
    }
  } catch {
    // Invalid URL
    return '';
  }
  
  return '';
}
